import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Sparkles } from "lucide-react";

const STORAGE_KEY = "cg_fortis_popup_dismissed_v1";

/**
 * Announcement modal shown on the home page.
 * Dismissal persisted in sessionStorage so it only shows once per session.
 */
const AnnouncementModal = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (e) {
      /* ignore */
    }
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setOpen(false);
  };

  const goContact = () => {
    close();
    // Use navigate so LandingPage's hash-effect scrolls into view
    setTimeout(() => navigate("/#contact"), 50);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="announcement-title"
      data-testid="announcement-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close announcement"
        onClick={close}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
        data-testid="announcement-backdrop"
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden cg-fade-up max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="relative bg-[#E50914] px-6 py-5 text-white overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 cg-halftone-white opacity-25 pointer-events-none" />
          <div className="relative flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 border border-white/30">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 pr-7">
              <div className="text-[11px] font-semibold uppercase tracking-widest opacity-85">
                Announcement
              </div>
              <h3
                id="announcement-title"
                className="font-[Manrope] font-extrabold text-xl sm:text-2xl leading-tight mt-0.5"
              >
                FortisBC Dual Fuel Rebates Are Back
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
            data-testid="announcement-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 text-[#1F2937] text-[15px] leading-relaxed overflow-y-auto">
          <p className="mb-4">
            As of <strong>October 15, 2026</strong>, FortisBC is bringing back their
            dual fuel heating system rebates for BC homeowners.
          </p>

          <div className="bg-[#FCE7E9] border-l-4 border-[#E50914] rounded-r-lg px-4 py-3 mb-4">
            <div className="font-bold text-[#E50914] text-sm uppercase tracking-wider mb-1">
              What this means for you
            </div>
            <p className="text-[#0F0F0F] text-sm">
              You may now be eligible for FortisBC dual fuel rebates when upgrading your
              heating system with Corona Gas. Our team is HPCN-trained and ready to help
              you take advantage of the new program.
            </p>
          </div>

          <p className="mb-2 font-semibold text-[#0F0F0F]">Want to learn more?</p>
          <p className="mb-0">
            Reach out and we'll walk you through what qualifies, what's needed, and the
            next steps for your home.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-3 flex-shrink-0 border-t border-[#EAEAEA] bg-[#FAFAFA]">
          <button
            type="button"
            onClick={close}
            className="btn-outline-coral justify-center sm:flex-1 mt-4"
            data-testid="announcement-dismiss"
          >
            Maybe Later
          </button>
          <button
            type="button"
            onClick={goContact}
            className="btn-coral justify-center sm:flex-1 mt-4"
            data-testid="announcement-contact"
          >
            Contact Us
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
